import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store/index';

const jwt = require('jsonwebtoken');

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    name: 'Login',
    component: () => import('../views/Login.vue'),
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('../views/Dashboard.vue'),
  },
  {
    path: '/mobile',
    name: 'Mobile',
    component: () => import('../views/Mobile.vue'),
  },
  {
    path: '/detail/:date',
    name: 'date',
    component: () => import('../views/Detail.vue'),
  },
  {
    path: '/detail',
    name: 'Detail',
    component: () => import('../views/Detail.vue'),
  },
  {
    path: '/reservation',
    name: 'Reservation',
    component: () => import('../views/Reservation.vue'),
  },
  {
    path: '/permission',
    name: 'Permission',
    component: () => import('../views/Permission.vue'),
  },
  {
    path: '/my-statistic',
    name: 'TechnicianStatistic',
    component: () => import('../views/TechnicianStatistic.vue'),
  },
  {
    path: '/statistic',
    name: 'Statistic',
    component: () => import('../views/Statistic.vue'),
  },
  {
    path: '/statisticdetail',
    name: 'StatisticDetail',
    component: () => import('../views/StatisticDetail.vue'),
  },
  {
    path: '/taskmanage',
    name: 'TaskManage',
    component: () => import('../views/TaskManage.vue'),
  },
  {
    path: '/assigned-tasks',
    name: 'AssignedTaskList',
    component: () => import('../views/AssignedTaskList.vue'),
  },
  {
    path: '/sales-info',
    name: 'SalesInfo',
    component: () => import('../views/SalesInfo.vue'),
  },
  {
    path: '/survey/home',
    name: 'SurveyHome',
    component: () => import('../views/survey/SurveyHome.vue'),
    meta: {
      requiresAuth: true,
      title: '滿意度首頁',
    },
  },
  {
    path: '/survey/overview',
    name: 'SurveyItemAnalysis',
    component: () => import('../views/survey/SurveyItemAnalysis.vue'),
    meta: {
      requiresAuth: true,
      title: '總體分析',
    },
  },
  {
    path: '/survey/item-analysis',
    name: 'SurveyItemAnalysis',
    component: () => import('../views/survey/SurveyItemAnalysis.vue'),
    meta: {
      requiresAuth: true,
      title: '項目分析',
    },
  },
  {
    path: '/s/:id',
    name: 'SurveyQuestionList',
    component: () => import('../views/survey/SurveyQuestionList.vue'),
    meta: {
      requiresAuth: false,
      public: true,
      title: '滿意度問卷詳情',
    },
  },
  {
    path: '/survey/question-management',
    name: 'SurveyQuestionManagement',
    component: () => import('../views/survey/SurveyQuestionManagement.vue'),
    meta: {
      requiresAuth: true,
      title: '問卷管理',
    },
  },
  {
    path: '/survey/question-design',
    name: 'SurveyQuestionDesign',
    component: () => import('../views/survey/SurveyQuestionDesign.vue'),
    meta: {
      requiresAuth: true,
      title: '問卷設計',
      requiresAdmin: true,
    },
  },
  {
    path: '/survey/group-message',
    name: 'SurveyGroupMessage',
    component: () => import('../views/survey/SurveyGroupMessage.vue'),
    meta: {
      requiresAuth: true,
      title: '簡訊',
    },
  },
  {
    path: '/survey/complaint',
    name: 'SurveyComplaint',
    component: () => import('../views/survey/SurveyComplaint.vue'),
    meta: {
      requiresAuth: true,
      requiresComplaint: true,
      title: '客訴管理',
    },
  },
  {
    path: '/survey/preview',
    name: 'SurveyPreview',
    component: () => import('../views/survey/SurveyQuestionList.vue'),
    meta: {
      requiresAuth: true,
      title: '問卷預覽',
    },
  },
  {
    path: '/survey/form/:id',
    name: 'SurveyForm',
    component: () => import('../views/survey/SurveyQuestionList.vue'),
    meta: {
      requiresAuth: false,
      public: true,
      title: '滿意度問卷',
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

// chunk 載入失敗（多半是快取到舊版 index/js）時自動重載一次拿新資源，
// 避免登入後 router-view 整塊空白
router.onError((err) => {
  const msg = (err && err.message) || '';
  if (/Loading( CSS)? chunk|ChunkLoadError|Importing a module script failed/i.test(msg)) {
    if (!sessionStorage.getItem('chunkReloaded')) {
      sessionStorage.setItem('chunkReloaded', '1');
      window.location.reload();
    }
  }
});
router.afterEach(() => {
  sessionStorage.removeItem('chunkReloaded');
});

let lastVersionCheck = 0;
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 最短每 5 分鐘才發一次請求

async function checkForUpdates() {
  const localVersion = localStorage.getItem('appVersion');
  const response = await fetch('/version.json');
  const data = await response.json();
  const serverVersion = data.version;

  if (localVersion !== serverVersion) {
    localStorage.setItem('appVersion', serverVersion);
    window.location.reload();
  }
}

router.beforeEach((to, from, next) => {
  const now = Date.now();
  if (now - lastVersionCheck > VERSION_CHECK_INTERVAL) {
    lastVersionCheck = now;
    checkForUpdates(); // 背景執行，不阻塞導航
  }
  const hasToken = localStorage.getItem('token');
  const position = localStorage.getItem('position');
  const encodeToken = jwt.decode(hasToken);
  const currentTime = new Date();

  if (to.matched.some((record) => record.meta.public)) {
    next();
    return;
  }

  if (to.matched.some((record) => record.meta.requiresAdmin) && position !== 'admin') {
    next({ name: 'SurveyHome' });
    return;
  }

  // 客訴管理：開放給 系統管理員 / 總經理 / 主管 / 業務（可互相指派、總經理結案）
  const COMPLAINT_ROLES = ['admin', 'gm', 'director', 'viewer'];
  if (to.matched.some((record) => record.meta.requiresComplaint)
    && !COMPLAINT_ROLES.includes(position)) {
    next({ name: 'SurveyHome' });
    return;
  }

  // 偵測到舊格式的 ability（非 JSON），強制清除並重新登入
  const abilityRaw = localStorage.getItem('ability');
  if (abilityRaw && abilityRaw.includes('[object Object]')) {
    localStorage.removeItem('token');
    localStorage.removeItem('position');
    localStorage.removeItem('name');
    localStorage.removeItem('email');
    localStorage.removeItem('id');
    localStorage.removeItem('image');
    localStorage.removeItem('region');
    localStorage.removeItem('ability');
    next({ path: '/' });
    return;
  }

  if (to.name !== 'Login') {
    if (encodeToken) {
      if (currentTime.getTime() > encodeToken.exp * 1000) {
        next({ path: '/' });
        store.commit('SET_EXPIRY_FIRE', true);
      } else {
        if (position) {
          store.state.userInfo.position = position;
          store.state.userInfo.name = localStorage.getItem('name');
          store.state.userInfo.email = localStorage.getItem('email');
          store.state.userInfo.id = localStorage.getItem('id');
          store.state.userInfo.region = localStorage.getItem('region');
          store.state.userInfo.image = localStorage.getItem('image');
          store.state.region = localStorage.getItem('currentRegion') || localStorage.getItem('region') || '北區';

          // 根據路由路徑設置不同的側邊欄
          if (to.path.startsWith('/survey') || to.path.startsWith('/s/')) {
            const surveySidebar = [
              {
                name: 'surveyHome',
                label: '滿意度首頁',
                url: '/survey/home',
                icon: 'mdi-home',
              },
              {
                name: 'surveyItemAnalysis',
                label: '問卷分析',
                url: '/survey/item-analysis',
                icon: 'mdi-chart-bar',
              },
              {
                name: 'surveyQuestionList',
                label: '問卷列表',
                url: '/survey/question-management',
                icon: 'mdi-format-list-bulleted',
              },
            ];

            if (position === 'admin') {
              surveySidebar.push({
                name: 'surveyQuestionDesign',
                label: '問卷設計',
                url: '/survey/question-design',
                icon: 'mdi-file-document-edit',
              });
            }

            if (['admin', 'gm', 'director', 'viewer'].includes(position)) {
              surveySidebar.push({
                name: 'surveyComplaint',
                label: '客訴管理',
                url: '/survey/complaint',
                icon: 'mdi-alert-octagon',
              });
            }

            surveySidebar.push(
              {
                name: 'surveyPreview',
                label: '問卷預覽',
                url: '/survey/preview',
                icon: 'mdi-file-eye',
              },
              {
                name: 'surveyGroupMessage',
                label: '簡訊',
                url: '/survey/group-message',
                icon: 'mdi-message-text',
              },
            );
            store.state.sideBar = surveySidebar;
          } else if (localStorage.getItem('ability') === undefined || localStorage.getItem('ability') === null) {
            if (position === 'viewer') {
              store.state.sideBar = [
                {
                  name: 'dashboard',
                  label: '主控台',
                  url: '/dashboard',
                  icon: 'mdi-view-dashboard',
                },
                {
                  name: 'reservation',
                  label: '預排行程',
                  url: '/reservation',
                  icon: 'mdi-calendar-today',
                },
                {
                  name: 'assignedTasks',
                  label: '已指派任務',
                  url: '/assigned-tasks',
                  icon: 'mdi-clipboard-list-outline',
                },
                {
                  name: 'statistic',
                  label: '統計分析',
                  url: '/statistic',
                  icon: 'mdi-chart-box',
                },
              ];
            } else {
              store.state.sideBar = [
                {
                  name: 'dashboard',
                  label: '主控台',
                  url: '/dashboard',
                  icon: 'mdi-view-dashboard',
                },
                {
                  name: 'reservation',
                  label: '預排行程',
                  url: '/reservation',
                  icon: 'mdi-calendar-today',
                },
                {
                  name: 'assignedTasks',
                  label: '已指派任務',
                  url: '/assigned-tasks',
                  icon: 'mdi-clipboard-list-outline',
                },
                {
                  name: 'permission',
                  label: '權限設定',
                  url: '/permission',
                  icon: 'mdi-lock-outline',
                },
                {
                  name: 'statistic',
                  label: '統計分析',
                  url: '/statistic',
                  icon: 'mdi-chart-box',
                },
                {
                  name: 'taskManage',
                  label: '任務管理',
                  url: '/taskmanage',
                  icon: 'mdi-cog',
                },
                {
                  name: 'salesInfo',
                  label: '業務資訊',
                  url: '/sales-info',
                  icon: 'mdi-account-tie',
                },
              ];
            }
          } else if (localStorage.getItem('ability').length > 0) {
            try {
              store.state.userInfo.ability = JSON.parse(localStorage.getItem('ability'));
            } catch (e) {
              store.state.userInfo.ability = [];
            }
            if (position === 'general') {
              store.state.sideBar = [
                {
                  name: 'mobile',
                  label: '施作',
                  url: '/mobile',
                  icon: 'mdi-account-wrench',
                },
                {
                  name: 'myStatistic',
                  label: '統計分析',
                  url: '/my-statistic',
                  icon: 'mdi-chart-box',
                },
              ];
            } else {
              store.state.sideBar = [
                {
                  name: 'mobile',
                  label: '施作',
                  url: '/mobile',
                  icon: 'mdi-account-wrench',
                },
                {
                  name: 'dashboard',
                  label: '主控台',
                  url: '/dashboard',
                  icon: 'mdi-view-dashboard',
                },
                {
                  name: 'reservation',
                  label: '預排行程',
                  url: '/reservation',
                  icon: 'mdi-calendar-today',
                },
                {
                  name: 'assignedTasks',
                  label: '已指派任務',
                  url: '/assigned-tasks',
                  icon: 'mdi-clipboard-list-outline',
                },
                {
                  name: 'permission',
                  label: '權限設定',
                  url: '/permission',
                  icon: 'mdi-lock-outline',
                },
                {
                  name: 'statistic',
                  label: '統計分析',
                  url: '/statistic',
                  icon: 'mdi-chart-box',
                },
                {
                  name: 'taskManage',
                  label: '任務管理',
                  url: '/taskmanage',
                  icon: 'mdi-cog',
                },
                {
                  name: 'salesInfo',
                  label: '業務資訊',
                  url: '/sales-info',
                  icon: 'mdi-account-tie',
                },
              ];
            }
          }
        }
        next();
      }
    } else {
      next({ path: '/' });
    }
  } else {
    next();
  }
});

export default router;
